import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { clone } from '@jack-henry/frontend-utils/functions';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { LookupModelDto } from '@treasury/api/channel';
import { AchCompany } from '@treasury/domain/ach';
import { AchService } from '@treasury/domain/ach/ach.service';
import { TmBaseComponent } from '@treasury/presentation';
import { type SelectItemVm, type TmValueChanged } from '@treasury/presentation/components/forms';
import type { CurrencyRange } from '@treasury/presentation/components/tm-currency-range';
import type { DateRange } from '@treasury/presentation/components/tm-date-range';
import { ButtonConfig } from '@treasury/presentation/components/tm-footer.types';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { achFrequencies } from '../data/ach-frequency-items';
import { AchPaymentFilterVm } from '../data/ach-payment-filter-vm';
import { achPaymentStatusItems } from '../data/ach-payment-status-items';
import { achTypeItems } from '../data/ach-type-items';

import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-bottom-sheet';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-currency-range';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-date-range';
import '@treasury/presentation/components/tm-footer';
import '@treasury/presentation/components/tm-loader';
import '@treasury/presentation/components/tm-multi-select';
import '@treasury/presentation/components/tm-select';
import '@treasury/presentation/components/tm-text-field';

const defaultState = {
    achCompany: true,
    debitAmount: true,
    creditAmount: true,
    initiatedDate: true,
    effectiveDate: true,
    createdDate: true,
    nextPaymentDate: true,
    frequency: true,
};

export const tagName = 'ach-payments-filter-sheet';
@customElement(tagName)
export class AchPaymentsFilterSheet extends TmBaseComponent {
    @property({ type: Boolean })
    public open = false;

    @property({ type: Object })
    public initial?: AchPaymentFilterVm;

    @InjectProperty()
    declare private achService: AchService;

    @state()
    private filter: AchPaymentFilterVm = new AchPaymentFilterVm();

    @state()
    private loading = true;

    @state()
    private isRecurring!: boolean;

    @state()
    private achType!: string;

    @state()
    private status!: string;

    @state()
    private achCompanies: AchCompany[] = [];

    @state()
    private achCompaniesSelected: AchCompany[] = [];

    @state()
    private batchName!: string;

    @state()
    private transactionId!: string;

    @state()
    private secCodeItems!: SelectItemVm<string>[];

    @state()
    private secCode!: string;

    @state()
    private debitAmount!: CurrencyRange;

    @state()
    private creditAmount!: CurrencyRange;

    @state()
    private initiatedDateRange!: DateRange;

    @state()
    private effectiveDateRange!: DateRange;

    @state()
    private frequenciesSelected: LookupModelDto[] = [];

    @state()
    private nextPaymentDateRange!: DateRange;

    @DeepReactive()
    private validState = defaultState;

    protected async firstUpdated() {
        try {
            this.achCompanies = await this.achService.getAchCompanies();
            this.achCompaniesSelected = [...this.achCompanies];

            const secCodes = await this.achService.getSecCodes();
            this.secCodeItems = secCodes.map(secCode => ({
                label: secCode,
                value: secCode,
            }));
        } finally {
            if (this.initial) {
                this.filter = clone(this.initial);
            }
            if (!this.filter.achCompanies.length) {
                this.filter.achCompanies = [...this.achCompanies];
            }
            if (this.filter.isRecurring && !this.filter.frequencies) {
                this.filter.frequencies = [...achFrequencies];
            }
            this.unpackFilter();
            this.onApplyFilters();

            this.loading = false;
        }
    }

    protected render() {
        return html`<tm-bottom-sheet
            .open=${this.open}
            @close=${() => {
                this.open = false;
                this.dispatchEvent(new CustomEvent('close'));
            }}
            >${this.renderHeader()}
            <p slot="header-right"></p>
            <tm-section class="p-3 mb-10">${this.renderForm()}</tm-section>
            <tm-footer
                .buttonConfig=${[
                    {
                        text: 'Apply Filters',
                        onClick: () => {
                            this.onApplyFilters();
                        },
                        disabled: Object.values(this.validState).some(isValid => !isValid),
                    },
                    {
                        importance: 'secondary',
                        text: 'Reset',
                        onClick: () => {
                            this.onResetForm();
                        },
                    },
                ] as ButtonConfig[]}
            ></tm-footer>
        </tm-bottom-sheet>`;
    }

    private onApplyFilters() {
        this.packFilter();
        this.dispatchEvent(
            new CustomEvent('applyFilters', {
                detail: this.filter,
                bubbles: true,
                composed: true,
            })
        );
        this.dispatchEvent(new CustomEvent('close'));
    }

    private onResetForm() {
        this.filter.reset();
        this.filter.achCompanies = [...this.achCompanies];
        if (this.filter.isRecurring) {
            this.filter.frequencies = [...achFrequencies];
        }
        this.unpackFilter();
        this.validState = { ...defaultState };
    }

    private unpackFilter() {
        this.isRecurring = this.filter.isRecurring;
        this.achType = this.filter.achType;
        this.status = this.filter.status;
        this.achCompaniesSelected = [...this.filter.achCompanies];
        this.batchName = this.filter.batchName;
        this.transactionId = this.filter.transactionId;
        this.secCode = this.filter.secCode;
        this.debitAmount = this.filter.debitRange;
        this.creditAmount = this.filter.creditRange;
        this.initiatedDateRange = this.filter.initiatedRange;
        this.effectiveDateRange = this.filter.effectiveRange;
        this.frequenciesSelected = achFrequencies.filter(
            i => this.filter.frequencies && this.filter.frequencies.some(f => f.id === i.id)
        );
        this.nextPaymentDateRange = this.filter.nextPaymentRange;
    }

    private packFilter() {
        this.filter.isRecurring = this.isRecurring;
        this.filter.achType = this.achType;
        this.filter.status = this.status;
        this.filter.achCompanies = [...this.achCompaniesSelected];
        this.filter.batchName = this.batchName;
        this.filter.transactionId = this.transactionId;
        this.filter.secCode = this.secCode;
        this.filter.debitRange = this.debitAmount;
        this.filter.creditRange = this.creditAmount;
        this.filter.initiatedRange = this.initiatedDateRange;
        this.filter.effectiveRange = this.effectiveDateRange;
        this.filter.frequencies = [...this.frequenciesSelected];
        this.filter.nextPaymentRange = this.nextPaymentDateRange;
    }

    private renderHeader() {
        if (!this.filter) {
            return nothing;
        }
        return html`<p slot="header-center">
            Filter ${this.filter.isRecurring ? 'Recurring' : nothing} ACH Payments
        </p>`;
    }

    private renderForm() {
        if (this.loading || !this.filter) {
            return html`<tm-loader card></tm-loader>`;
        }

        return html`<form>
            ${!this.isRecurring
                ? html`<tm-select
                      label="ACH Type"
                      .items=${achTypeItems}
                      .initial=${this.achType}
                      @value-changed=${(e: TmValueChanged<string>) => {
                          if (e.detail.value) {
                              this.achType = e.detail.value;
                          }
                      }}
                  >
                  </tm-select>`
                : nothing}
            <tm-select
                label="Status"
                .items=${achPaymentStatusItems}
                .initial=${this.status}
                @value-changed=${(e: TmValueChanged<string>) => {
                    if (e.detail.value) {
                        this.status = e.detail.value;
                    }
                }}
            >
            </tm-select>
            <tm-multi-select
                label="ACH Company"
                .allowSelectAll=${true}
                .allSelectedText=${'All ACH Companies'}
                .mapItem=${mapCompanyToVm}
                .items=${this.achCompanies}
                .initial=${this.achCompaniesSelected}
                @value-changed=${(e: TmValueChanged<AchCompany[]>) => {
                    this.achCompaniesSelected = e.detail.value || [];
                    this.validState.achCompany = this.achCompaniesSelected.length > 0;
                }}
            >
            </tm-multi-select>
            <tm-text-field
                label="Batch Name"
                .initial=${this.batchName}
                @value-changed=${(e: CustomEvent) => {
                    this.batchName = e.detail.value;
                }}
            ></tm-text-field>
            <tm-text-field
                label="Transaction ID"
                .initial=${this.transactionId}
                @value-changed=${(e: CustomEvent) => {
                    this.transactionId = e.detail.value;
                }}
            ></tm-text-field>
            <tm-select
                label="SEC Code"
                .items=${this.secCodeItems}
                .initial=${this.secCode}
                @value-changed=${(e: TmValueChanged<string>) => {
                    if (e.detail.value) {
                        this.secCode = e.detail.value;
                    }
                }}
            >
            </tm-select>
            <tm-currency-range
                label="Debit Amount"
                .initial=${this.debitAmount}
                @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                    const { isValid, value } = e.detail;
                    this.validState.debitAmount = isValid;
                    if (!value) {
                        return;
                    }
                    this.debitAmount = value;
                }}
            ></tm-currency-range>
            <tm-currency-range
                label="Credit Amount"
                .initial=${this.creditAmount}
                @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                    const { isValid, value } = e.detail;
                    this.validState.creditAmount = isValid;
                    if (!value) {
                        return;
                    }
                    this.creditAmount = value;
                }}
            ></tm-currency-range>
            <tm-date-range
                .label=${this.isRecurring ? 'Created Date' : 'Initiated Date'}
                .initial=${this.initiatedDateRange}
                @value-changed=${(e: TmValueChanged<DateRange>) => {
                    const { isValid, value } = e.detail;
                    this.validState.initiatedDate = isValid;
                    if (value) {
                        this.initiatedDateRange = value;
                    }
                }}
            ></tm-date-range>

            ${this.isRecurring
                ? html` <tm-multi-select
                          label="Frequency"
                          .allowSelectAll=${true}
                          .allSelectedText=${'All Frequencies'}
                          .items=${achFrequencies}
                          .initial=${this.frequenciesSelected}
                          .mapItem=${(item: LookupModelDto) => ({
                              label: item.value,
                              value: item,
                          })}
                          @value-changed=${(e: TmValueChanged<LookupModelDto[]>) => {
                              this.frequenciesSelected = e.detail.value || [];
                              this.validState.frequency = this.frequenciesSelected.length > 0;
                          }}
                      ></tm-multi-select
                      ><tm-date-range
                          label="Next Payment Date"
                          .initial=${this.nextPaymentDateRange}
                          @value-changed=${(e: TmValueChanged<DateRange>) => {
                              const { isValid, value } = e.detail;
                              this.validState.nextPaymentDate = isValid;
                              if (value) {
                                  this.nextPaymentDateRange = value;
                              }
                          }}
                      ></tm-date-range>`
                : html`<tm-date-range
                      label="Effective Date"
                      .initial=${this.effectiveDateRange}
                      @value-changed=${(e: TmValueChanged<DateRange>) => {
                          const { isValid, value } = e.detail;
                          this.validState.effectiveDate = isValid;
                          if (value) {
                              this.effectiveDateRange = value;
                          }
                      }}
                  ></tm-date-range>`}
        </form>`;
    }
}

function mapCompanyToVm(company: AchCompany): SelectItemVm<AchCompany> {
    return {
        label: company.companyName,
        value: company,
    };
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchPaymentsFilterSheet;
    }
}
